import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { AMBIENT_SCRIBE_CUSTOM_TEMPLATES } from "constants/feature-flags";
import { ScribeTemplateTypeLabel } from "features/medical-scribe/components/scribe-template-type-label.component";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import FieldLabel from "features/staff-tasks-v2/drawer/staff-task-card/field-label.component";
import {
  AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
  useGenerateAudiologyReportSummaryMutation,
  useGetAmbientScribeTemplatesQuery
} from "services/ambient-scribe";
import { AMBIENT_SCRIBE_TEMPLATE_TYPE } from "services/types/ambient-scribe-template.type";

/** The built-in History / Results / Recommendations prompt — no template needed. */
const BUILT_IN_TEMPLATE_VALUE = "";
const BUILT_IN_TEMPLATE_LABEL = "Suno default summary";

// `GET /templates/` defaults to a page size of 5, so a practice with more audiology templates
// would silently lose entries from this picker.
const AUDIOLOGY_TEMPLATES_QUERY_PARAMS = {
  limit: 300,
  offset: 0,
  ordering: "-pk",
  query: AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
  type: AMBIENT_SCRIBE_TEMPLATE_TYPE.AUDIOLOGY_REPORT
};

type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

type AudiologySummaryGenerateSectionProps = {
  scribeId: number;
  reportId: number;
  /** Union of the local latch and `is_generating_audiology_summary` from the scribe. */
  isGenerating: boolean;
  /**
   * True once a summary exists, so the button reads **Apply** — at that point the action is
   * "use this template on this report", not a first generation. The empty state keeps
   * "Generate Summary": "Apply" with nothing generated yet has no referent.
   */
  hasExistingSummary: boolean;
  /**
   * Controlled by the tab, not held here. The tab renders this section from two different
   * branches (empty state vs. alongside a summary), so React remounts it when the first summary
   * arrives — local state would silently reset the user's template right before the moment they
   * are most likely to regenerate with it. `null` means the built-in prompt, which is a real
   * choice; the tab distinguishes it from "not yet seeded" on its own side.
   */
  selectedTemplateId: number | null;
  onSelectedTemplateIdChange: (id: number | null) => void;
  onGenerateStarted: () => void;
};

export default function AudiologySummaryGenerateSection({
  scribeId,
  reportId,
  isGenerating,
  hasExistingSummary,
  selectedTemplateId,
  onSelectedTemplateIdChange,
  onGenerateStarted
}: Readonly<AudiologySummaryGenerateSectionProps>) {
  const theme = useTheme();
  const flags = useFlags();
  // This flag's whole meaning is "users may choose templates", so it hides the PICKER — but not
  // the button: generating with no template is a first-class path (the backend falls back to its
  // built-in prompt), so the feature stays fully usable with the flag off.
  const canChooseTemplate = Boolean(flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES]);

  const [generateError, setGenerateError] = useState<GenerateError | null>(
    null
  );

  const { data: templates = [] } = useGetAmbientScribeTemplatesQuery(
    AUDIOLOGY_TEMPLATES_QUERY_PARAMS,
    { refetchOnMountOrArgChange: true, skip: !canChooseTemplate }
  );

  const [generateSummary, { isLoading: isRequestInFlight }] =
    useGenerateAudiologyReportSummaryMutation();

  // A template deleted while the picker was open must not be sent.
  const templateId = useMemo(() => {
    if (selectedTemplateId == null) return null;
    return templates.some((t) => t.id === selectedTemplateId)
      ? selectedTemplateId
      : null;
  }, [selectedTemplateId, templates]);

  const isBusy = isGenerating || isRequestInFlight;

  const handleGenerate = async () => {
    const result = await generateSummary({
      scribe_id: scribeId,
      report_id: reportId,
      ...(templateId != null
        ? { ambient_scribe_template_id: templateId }
        : undefined)
    });

    if ("error" in result) {
      const err = result.error as
        | {
            status?: number;
            data?: {
              error?: string;
              message?: string;
              subscription_state?: string;
            };
          }
        | undefined;

      // 409 is "one is already running for this scribe", not a failure — the lock is per scribe
      // and self-heals after 15 minutes. Showing the spinner is the correct response; an error
      // dialog here would teach users to double-click their way into confusion.
      if (err?.status === 409) {
        onGenerateStarted();
        return;
      }
      if (err?.status === 402 && err?.data?.subscription_state) {
        setGenerateError({
          kind: "subscription",
          error: err.data.error ?? "Subscription required",
          message: err.data.message ?? ""
        });
        return;
      }
      setGenerateError({ kind: "generic" });
      return;
    }

    onGenerateStarted();
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems={canChooseTemplate ? "flex-end" : "center"}
        flexWrap="wrap"
      >
        {canChooseTemplate && (
          <Stack>
            <FieldLabel
              label="Select Scribe Template"
              sx={{ mb: "3px", mt: -4 }}
            />
            {/*
              Deliberately NOT disabled while a generation runs. Changing the selection mid-run
              costs nothing — the Apply button below carries the real guard — and locking it made
              the picker unreadable at exactly the moment a user wants to check what produced the
              summary they are waiting on.
            */}
            <FormControl size="small" sx={{ minWidth: 280, maxWidth: 400 }}>
              <Select
                id="audiology-summary-template-select"
                data-testid="audiology-summary-template-select"
                value={templateId ?? BUILT_IN_TEMPLATE_VALUE}
                displayEmpty
                sx={{
                  "& .MuiOutlinedInput-notchedOutline legend": {
                    display: "none"
                  },
                  "& .MuiOutlinedInput-notchedOutline": { top: 0 }
                }}
                onChange={(e) => {
                  const next = e.target.value;
                  onSelectedTemplateIdChange(
                    next === BUILT_IN_TEMPLATE_VALUE ? null : Number(next)
                  );
                }}
                MenuProps={{
                  sx: { zIndex: 9999 },
                  PaperProps: { sx: { zIndex: 9999, maxHeight: 360 } }
                }}
                // The closed control shows the name only, so the two-line options below cannot
                // change its height.
                renderValue={(selected) =>
                  selected === BUILT_IN_TEMPLATE_VALUE
                    ? BUILT_IN_TEMPLATE_LABEL
                    : (templates.find((t) => t.id === Number(selected))?.name ??
                      "")
                }
              >
                {/* Selectable, not a disabled placeholder — "no template" is a real choice.
                    No type label: this is the backend's built-in prompt, not a template. */}
                <MenuItem value={BUILT_IN_TEMPLATE_VALUE}>
                  {BUILT_IN_TEMPLATE_LABEL}
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem
                    key={template.id}
                    value={template.id}
                    sx={{ alignItems: "flex-start", py: 1 }}
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body3" fontWeight={700}>
                        {template.name}
                      </Typography>
                      {/* Constant across this list — it is already `?type=AudiologyReport`
                          filtered — but it keeps this picker consistent with the chart-note one
                          and makes the type explicit at the point of choosing. */}
                      <ScribeTemplateTypeLabel
                        type={template.type}
                        variant="caption"
                        muted
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          loading={isBusy}
          disabled={isBusy}
          size="small"
          data-testid="generate-audiology-summary-button"
        >
          {hasExistingSummary ? "Apply" : "Generate Summary"}
        </LoadingButton>
      </Stack>

      <MuiDialog
        open={generateError !== null}
        onClose={() => setGenerateError(null)}
        sx={{ zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) }}
      >
        <DialogTitle>
          {generateError?.kind === "subscription"
            ? "Subscription Required"
            : "Generation Failed"}
        </DialogTitle>
        <DialogContent>
          {generateError?.kind === "subscription" ? (
            <Alert severity="warning">
              <AlertTitle>{generateError.error}</AlertTitle>
              {generateError.message}
            </Alert>
          ) : (
            <Typography>
              Something went wrong while processing your request. Please try
              again in a few moments. If the issue persists, contact Suno
              support for assistance.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateError(null)}>Close</Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
